import './index.css';
import { useRef, useCallback } from 'react';

/**
 * При изменении размера содержимого дропа необходимо перезапускать пересчет позиции.
 * Для отслеживания изменения размер используется ResizeObserver вызывающий код обновления позиции и размера дропа.
 * Проблема в том, что нельзя просто подписаться на изменение размера самой внешней обертки дропа, т.к. для нее
 * размер жестко задается кодом отвечающим за позиционирование. Т.е. даже при изменении размера содержимого внешняя
 * обертка не поменяет свой размер. Кроме того в дропе может быть несколько независимых частей размер которых
 * может измениться. Этот хук возвращает фабрику refcallback'ов. При вызове фабрики создается refcallback который
 * подписывает ResizeObserver на изменение размера элемента. При передаче в аргументе для фабрики одного и того же
 * id, будет возвращен тот же самый refcallback, т.е. проблемы с изменением ссылки передаваемой в проп не возникает.
 * Далее эта фабрика передается в рендер функцию Drop/Tooltip и используется для подписки на изменение размера
 * различных частей.
 */
const useMonitorResize = (dropMetricsRef, updateMetricsRef) => {
    const resizeObserverRef = useRef();
    const getResizeObserver = useCallback(() => {
        if (!resizeObserverRef.current) {
            resizeObserverRef.current = new ResizeObserver(() => {
                dropMetricsRef.current = undefined;
                updateMetricsRef.current();
            });
        }
        return resizeObserverRef.current;
    }, [dropMetricsRef, updateMetricsRef]);
    const observedElementsInfoMapRef = useRef();
    const getObservedElementsInfoMap = useCallback(() => {
        observedElementsInfoMapRef.current = observedElementsInfoMapRef.current || new Map();
        return observedElementsInfoMapRef.current;
    }, []);
    const observeResizeRefCallbackFactory = (id, observe = true, isScrollable = false) => {
        if (getObservedElementsInfoMap().has(id)) {
            return getObservedElementsInfoMap().get(id)?.refCallback;
        }
        const refCallback = (node) => {
            const info = getObservedElementsInfoMap().get(id);
            const currentNode = info.node;
            if (currentNode === node) {
                return;
            }
            currentNode && observe && getResizeObserver().unobserve(currentNode);
            info.node = node;
            node && observe && getResizeObserver().observe(node);
        };
        getObservedElementsInfoMap().set(id, {
            node: null,
            refCallback,
            isScrollable,
        });
        return refCallback;
    };
    return {
        observeResizeRefCallbackFactory,
        getResizeObserver,
        getObservedElementsInfoMap,
    };
};

export { useMonitorResize };
//# sourceMappingURL=useMonitorResize.js.map
